import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AuthSuccess from "@/pages/AuthSuccess";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import PublicInvoice from "@/pages/PublicInvoice";
import MandateInstallmentInvoice from "@/pages/MandateInstallmentInvoice";
import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PaymentLinks from "@/pages/admin/PaymentLinks";
import ManualPayments from "@/pages/admin/ManualPayments";
import PaymentsLog from "@/pages/admin/PaymentsLog";
import ApiKeys from "@/pages/admin/ApiKeys";
import Websites from "@/pages/admin/Websites";
import WebhookLogs from "@/pages/admin/WebhookLogs";
import AuditLogs from "@/pages/admin/AuditLogs";
import Branches from "@/pages/admin/Branches";
import Settings from "@/pages/admin/Settings";
import Refunds from "@/pages/admin/Refunds";
import Customers from "@/pages/admin/Customers";
import CustomerDetail from "@/pages/admin/CustomerDetail";
import Roles from "@/pages/admin/Roles";
import Reports from "@/pages/admin/Reports";
import Statement from "@/pages/customer/Statement";
import CustomerFeedback from "@/pages/customer/CustomerFeedback";
import FeedbackAdmin from "@/pages/admin/FeedbackAdmin";
import Mandates from "@/pages/admin/Mandates";
import InstallmentHistory from "@/pages/admin/InstallmentHistory";
import CustomerLayout from "@/layouts/CustomerLayout";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerTransactions from "@/pages/customer/CustomerTransactions";
import CustomerProfile from "@/pages/customer/CustomerProfile";
import CustomerMandates from "@/pages/customer/CustomerMandates";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#fbce18] uppercase tracking-[0.2em] text-xs animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "customer" ? "/customer" : "/dashboard"} replace />;
  }
  return children;
}

function AppRouter() {
return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/invoice/:invoiceNo" element={<PublicInvoice />} />
      <Route
        path="/mandate-invoice/:mandateId/:installmentId"
        element={
          <Protected roles={["customer", "admin", "staff"]}>
            <MandateInstallmentInvoice />
          </Protected>
        }
      />

      <Route path="/dashboard" element={<Protected roles={["admin", "staff"]}><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="payment-links" element={<PaymentLinks />} />
        <Route path="mandates" element={<Mandates />} />
        <Route path="installment-history" element={<InstallmentHistory />} />
        <Route path="manual-payments" element={<ManualPayments />} />
        <Route path="payments" element={<PaymentsLog />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:email" element={<CustomerDetail />} />
        <Route path="refunds" element={<Refunds />} />
        <Route path="branches" element={<Branches />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="websites" element={<Websites />} />
        <Route path="webhooks" element={<WebhookLogs />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="reports" element={<Reports />} />
        <Route path="roles" element={<Roles />} />
        <Route path="feedback" element={<FeedbackAdmin />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="/customer" element={<Protected roles={["customer", "admin", "staff"]}><CustomerLayout /></Protected>}>
        <Route index element={<CustomerDashboard />} />
        <Route path="transactions" element={<CustomerTransactions />} />
        <Route path="mandates" element={<CustomerMandates />} />
        <Route path="statement" element={<Statement />} />
        <Route path="feedback" element={<CustomerFeedback />} />
        <Route path="profile" element={<CustomerProfile />} />
      </Route>

      <Route
        path="*"
        element={<Navigate to="/dashboard" replace />}
      />

<Route
  path="/auth-success"
  element={<AuthSuccess />}
/>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: "rgba(20,20,20,0.9)",
              border: "1px solid rgba(251,206,24,0.25)",
              color: "#f8f8f8",
              backdropFilter: "blur(16px)",
            },
          }}
        />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
